// Shape checks for a 2xx payload that callApi hands back UNCHECKED. callApi returns
// response.text() cast to T for any 2xx without a JSON content type, so an HTML page from a
// proxy, or an empty 200, lands in a caller typed as an array. Published to an atom, that
// reaches a render path as something .map() cannot take, and the customer app becomes
// GlobalError. Extracted from the byte-alike copies in useProductSuggestApi,
// useLegalClassRestrictionApi and useProductFacetApi (rule of three — see apiFailure.ts for
// the same precedent).
//
// Both throw rather than return a verdict: every caller decides its outcome in a try and
// publishes it in a finally, so a throw is what makes a bad payload publish `failed` like any
// other failed read. Deliberately free of useApi imports, as apiFailure.ts is.

const unexpectedResponse = (endpoint: string, expected: string): Error =>
    new Error(`Unexpected ${endpoint} response: expected ${expected}.`);

// The payload itself must be an array (a flat list endpoint with no paging envelope).
export const assertArrayPayload = (
    endpoint: string,
    payload: unknown,
): void => {
    if (!Array.isArray(payload)) {
        throw unexpectedResponse(endpoint, "an array");
    }
};

// The payload must be a plain object (not an array). For a caller that then checks its fields
// one by one and degrades per field rather than failing the whole read (GET /promotions/home).
export function assertObjectPayload(
    endpoint: string,
    payload: unknown,
): asserts payload is Record<string, unknown> {
    if (
        typeof payload !== "object" ||
        payload === null ||
        Array.isArray(payload)
    ) {
        throw unexpectedResponse(endpoint, "an object");
    }
}

// The payload must be an object carrying an array at EVERY named field. All fields are
// checked, not just the object: a payload carrying only some of them is still unusable, and
// the section that lost its array is the one that breaks.
export const assertArrayFields = (
    endpoint: string,
    payload: unknown,
    fields: readonly string[],
): void => {
    const record =
        typeof payload === "object" && payload !== null
            ? (payload as Record<string, unknown>)
            : undefined;

    if (!record || fields.some((field) => !Array.isArray(record[field]))) {
        throw unexpectedResponse(endpoint, `arrays at ${fields.join(", ")}`);
    }
};
